import EventFormModal from "@/components/enhancer/EventFormModal";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Users, ShoppingCart, ReceiptText, Sparkles, LogOut, UserCheck, CheckCircle2, XCircle, AlertTriangle, Loader2, Boxes, CalendarCheck, BarChart3, RotateCcw, Wallet, CalendarRange, MapPin, CalendarPlus, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useActiveContext } from "@/context/ActiveContext";
import ContextSwitcher from "@/components/ContextSwitcher";
import api from "@/lib/api";
import { paymentSummary, PAYMENT_TONE } from "@/lib/payment";
import { toast } from "sonner";

// Shared toast handling for appointment status changes (auto package redemption on complete).
function notifyStatusResult(res, status, id, qc, refresh) {
  if (status === "completed" && res?.auto_redeemed) {
    toast.success(
      `Package credit applied from ${res.package_name}. ${res.sessions_remaining} session${res.sessions_remaining === 1 ? "" : "s"} remaining.`,
      {
        duration: 12000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data: r } = await api.post(`/pos/appointments/${id}/reverse-redemption`);
              toast.success(`Redemption reversed — credit restored (${r.sessions_remaining} left). Balance due ${money(Number(r.balance_due || 0))}.`);
              refresh();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Couldn't reverse the redemption.");
            }
          },
        },
      },
    );
  } else if (status === "completed" && Number(res?.balance_due) > 0) {
    toast.warning(`No package credit available — balance due ${money(Number(res.balance_due))}. Collect with Cash/Card on the appointment.`, { duration: 9000 });
  } else {
    toast.success(`Marked ${status.replace("_", " ")}`);
  }
}
import POSRegister from "@/pages/pos/POSRegister";
import POSClients from "@/pages/pos/POSClients";
import POSInventory from "@/pages/pos/POSInventory";
import POSEvents from "@/pages/pos/POSEvents";
import POSReports from "@/pages/pos/POSReports";
import RefundModal from "@/pages/pos/RefundModal";
import POSBalances from "@/pages/pos/POSBalances";
import POSOutstanding from "@/pages/pos/POSOutstanding";
import POSGiftCards from "@/pages/pos/POSGiftCards";
import { Gift } from "lucide-react";
import UnifiedSearch from "@/components/pos/UnifiedSearch";
import NoShowModal from "@/components/pos/NoShowModal";
import AppointmentRefundModal from "@/pages/pos/AppointmentRefundModal";
import AddAppointmentModal from "@/components/pos/AddAppointmentModal";
import RescheduleModal from "@/components/pos/RescheduleModal";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const TABS = [
  { key: "desk", label: "Front Desk", icon: LayoutDashboard },
  { key: "schedule", label: "Schedule", icon: CalendarRange },
  { key: "outstanding", label: "Outstanding", icon: Wallet },
  { key: "giftcards", label: "Gift cards", icon: Gift },
  { key: "register", label: "Register", icon: ShoppingCart },
  { key: "clients", label: "Clients", icon: Users },
  { key: "sales", label: "Sales", icon: ReceiptText },
  { key: "balances", label: "Balances", icon: Wallet },
  { key: "inventory", label: "Inventory", icon: Boxes },
  { key: "events", label: "Events", icon: CalendarCheck },
  { key: "reports", label: "Reports", icon: BarChart3 },
];

function Stat({ label, value }) {
  return <div className="eyw-card p-5"><div className="text-3xl font-serif text-[#144F75]">{value}</div><p className="text-xs text-[#4A5D6B] mt-1">{label}</p></div>;
}

function FrontDesk() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [noShow, setNoShow] = useState(null);
  const [refundAppt, setRefundAppt] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["pos-dashboard"], queryFn: () => api.get("/pos/dashboard").then((r) => r.data), refetchInterval: 30000 });

  const setStatus = async (id, status) => {
    if (status === "no_show") { setNoShow(id); return; }
    try {
      const { data: res } = await api.put(`/pos/appointments/${id}/status`, { status });
      qc.invalidateQueries({ queryKey: ["pos-dashboard"] });
      notifyStatusResult(res, status, id, qc, () => qc.invalidateQueries({ queryKey: ["pos-dashboard"] }));
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Couldn't update the appointment.");
    }
  };

  if (isLoading || !data) return <div className="py-16 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>;
  const s = data.stats;
  return (
    <div data-testid="pos-frontdesk">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Stat label="Today's appts" value={s.appointments} />
        <Stat label="Checked in" value={s.checked_in} />
        <Stat label="Walk-ins" value={s.walk_ins} />
        <Stat label="Completed" value={s.completed} />
        <Stat label="Revenue today" value={`${money(Number(s.revenue_today || 0))}`} />
        <Stat label="Tips today" value={`${money(Number(s.tips_today || 0))}`} />
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
        <div className="eyw-card p-6">
          <h3 className="font-serif text-xl text-[#1A242B] mb-4">Today's schedule — {fmtDate(data.date)}</h3>
          {data.appointments.length === 0 ? <p className="text-sm text-[#4A5D6B]">No appointments today.</p> : (
            <ul className="divide-y divide-[#E5E0D8]" data-testid="desk-appointments">
              {data.appointments.map((a) => (
                <li key={a.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#1A242B] truncate">{fmtTimeRange(a.start_time)} · {a.service_title}</div>
                    <div className="text-xs text-[#4A5D6B]">{a.name || "—"} · <span className="capitalize">{a.status}</span></div>
                    {(() => { const p = paymentSummary(a); return <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${PAYMENT_TONE[p.tone]}`} data-testid={`desk-payment-${a.id}`}>{p.label}</span>; })()}
                  </div>
                  <div className="flex gap-1 shrink-0 items-center">
                    {a.status === "refunded" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#B45151]/15 text-[#B45151] font-semibold" data-testid={`appt-refunded-badge-${a.id}`}>Refunded</span>}
                    {canRefundRole(user) && apptRefundable(a) && (
                      <button title={a.payment_status === "package" ? "Restore credit" : "Refund"} onClick={() => setRefundAppt(a.id)} data-testid={`appt-refund-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#B45151] hover:bg-[#B45151]/10"><RotateCcw size={15} /></button>
                    )}
                    <button title="Check in" onClick={() => setStatus(a.id, "checked_in")} data-testid={`appt-checkin-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#144F75] hover:bg-[#144F75]/10"><UserCheck size={15} /></button>
                    <button title="Complete" onClick={() => setStatus(a.id, "completed")} data-testid={`appt-complete-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#5C8A72] hover:bg-[#5C8A72]/10"><CheckCircle2 size={15} /></button>
                    <button title="No-show" onClick={() => setStatus(a.id, "no_show")} data-testid={`appt-noshow-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#B5794E] hover:bg-[#D98E73]/10"><XCircle size={15} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-6">
          <div className="eyw-card p-6" data-testid="desk-room-usage">
            <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><CalendarCheck size={16} className="text-[#144F75]" /> Room usage today</h3>
            {(data.room_usage || []).length ? (
              <div className="space-y-3">
                {data.room_usage.map((r) => (
                  <div key={r.room_key} data-testid={`room-usage-${r.room_key}`}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#144F75] mb-1">{r.room_name}</div>
                    <ul className="space-y-1">
                      {r.blocks.map((b, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span className="text-[#8FA89B] tabular-nums shrink-0">{b.start}</span>
                          <span className="min-w-0">
                            <span className="text-[#1A242B]">{b.label}</span>
                            {b.who && <span className="text-[#4A5D6B]"> · {b.who}</span>}
                            {b.kind === "reservation" && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${b.pending ? "bg-[#D98E73]/15 text-[#B5794E]" : "bg-[#5C8A72]/15 text-[#5C8A72]"}`}>{b.pending ? "pending" : "reserved"}</span>}
                            {b.kind === "event" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#144F75]/10 text-[#144F75]">event</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#4A5D6B]">No rooms in use today.</p>}
          </div>
          <div className="eyw-card p-6">
            <h3 className="font-serif text-lg text-[#1A242B] mb-3">Outstanding balances</h3>
            {data.outstanding.length ? <ul className="space-y-2">{data.outstanding.map((o) => (
              <li key={o.id} className="text-sm flex justify-between"><span className="text-[#1A242B]">{o.client_name || "Walk-in"}</span><span className="text-[#B5794E]">{money((o.balance_due || o.total || 0))}</span></li>
            ))}</ul> : <p className="text-sm text-[#4A5D6B]">All settled.</p>}
          </div>
          <div className="eyw-card p-6">
            <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-[#B5794E]" /> Low inventory</h3>
            {data.low_inventory.length ? <ul className="space-y-2">{data.low_inventory.map((p) => (
              <li key={p.id} className="text-sm flex justify-between"><span className="text-[#1A242B] truncate">{p.title}</span><span className="text-[#B5794E]">{p.stock} left</span></li>
            ))}</ul> : <p className="text-sm text-[#4A5D6B]">Stock levels healthy.</p>}
          </div>
        </div>
      </div>
      {noShow && <NoShowModal bookingId={noShow} onClose={() => setNoShow(null)} onDone={() => { setNoShow(null); qc.invalidateQueries({ queryKey: ["pos-dashboard"] }); }} />}
      {refundAppt && <AppointmentRefundModal bookingId={refundAppt} onClose={() => setRefundAppt(null)} onDone={() => qc.invalidateQueries({ queryKey: ["pos-dashboard"] })} />}
    </div>
  );
}

function saleBy(s) {
  const names = [...new Set((s.items || []).map((i) => i.enhancer_name).filter(Boolean))];
  const base = names.length ? names : (s.assigned_to_name ? [s.assigned_to_name] : []);
  if (!base.length) return "";
  const suffix = base.length === 1 && s.assigned_to_type === "vendor" ? " (vendor)" : "";
  return base.join(", ") + suffix;
}

function SalesList() {
  const qc = useQueryClient();
  const [refundSale, setRefundSale] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["pos-sales"], queryFn: () => api.get("/pos/sales").then((r) => r.data) });
  if (isLoading) return <div className="py-16 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>;
  return (
    <div data-testid="pos-sales-list">
      <div className="eyw-card overflow-hidden hidden md:block">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F7F3] text-[#4A5D6B]"><tr>
          <th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Items</th>
          <th className="text-left px-4 py-3">Total</th><th className="text-left px-4 py-3">Cashier</th>
          <th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Date</th><th></th>
        </tr></thead>
        <tbody>
          {data.map((s) => (
            <tr key={s.id} className="border-t border-[#E5E0D8]" data-testid={`sale-row-${s.id}`}>
              <td className="px-4 py-3 text-[#1A242B]">{s.client_name || "Walk-in"}{saleBy(s) && <span className="block text-[10px] text-[#8FA89B]" data-testid={`sale-assigned-${s.id}`}>by {saleBy(s)}</span>}</td>
              <td className="px-4 py-3 text-[#4A5D6B]">{s.items?.length || 0}</td>
              <td className="px-4 py-3 text-[#144F75]">
                {s.package_redemption
                  ? <span className="inline-flex flex-col" data-testid={`sale-package-${s.id}`}>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#9E5C7F]/10 text-[#9E5C7F] font-medium w-fit">Package</span>
                      <span className="text-[10px] text-[#8FA89B] mt-0.5">{s.package_redemption.package_name} · {s.package_redemption.sessions_remaining_after} left</span>
                    </span>
                  : <>{money(Number(s.total || 0))}{Number(s.discount_total || 0) > 0 && <span className="block text-[10px] text-[#B5794E]" data-testid={`sale-discount-${s.id}`}>−${Number(s.discount_total).toFixed(2)}{(s.discounts || []).some((d) => d.kind === "coupon") ? ` · ${(s.discounts || []).find((d) => d.kind === "coupon").code}` : " discount"}</span>}{Number(s.tip || 0) > 0 && <span className="block text-[10px] text-[#5C8A72]">tip {money(Number(s.tip))}{s.tip_to_name ? ` → ${s.tip_to_name}` : ""}</span>}</>}
              </td>
              <td className="px-4 py-3 text-[#4A5D6B]">{s.cashier_name}</td>
              <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${s.payment_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{s.status}</span></td>
              <td className="px-4 py-3 text-[#8FA89B]">{s.created_at?.slice(0, 10)}</td>
              <td className="px-4 py-3 text-right">
                {["paid", "partial"].includes(s.payment_status) && s.status !== "refunded" && !s.package_redemption && (
                  <button onClick={() => setRefundSale(s)} data-testid={`sale-refund-${s.id}`} className="eyw-btn-ghost !py-1 !px-3 text-xs"><RotateCcw size={13} /> Refund</button>
                )}
              </td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#4A5D6B]">No sales yet.</td></tr>}
        </tbody>
      </table>
      </div>

      <div className="md:hidden space-y-3">
        {data.map((s) => (
          <div key={s.id} data-testid={`sale-card-${s.id}`} className="eyw-card p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="font-medium text-[#1A242B]">{s.client_name || "Walk-in"}{saleBy(s) && <span className="block text-[10px] font-normal text-[#8FA89B]">by {saleBy(s)}</span>}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${s.payment_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{s.status}</span>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Total</dt><dd className="text-[#144F75] font-semibold">{s.package_redemption ? `Package · ${s.package_redemption.package_name} (${s.package_redemption.sessions_remaining_after} left)` : `${money(Number(s.total || 0))}`}</dd></div>
              {Number(s.discount_total || 0) > 0 && <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Discount</dt><dd className="text-[#B5794E]" data-testid={`sale-discount-${s.id}`}>−${Number(s.discount_total).toFixed(2)}{(s.discounts || []).some((d) => d.kind === "coupon") ? ` · ${(s.discounts || []).find((d) => d.kind === "coupon").code}` : ""}</dd></div>}
              {Number(s.tip || 0) > 0 && <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Tip</dt><dd className="text-[#5C8A72]">{money(Number(s.tip))}{s.tip_to_name ? ` → ${s.tip_to_name}` : ""}</dd></div>}
              <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Items</dt><dd className="text-[#4A5D6B]">{s.items?.length || 0}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Cashier</dt><dd className="text-[#4A5D6B] break-words">{s.cashier_name}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Date</dt><dd className="text-[#8FA89B]">{s.created_at?.slice(0, 10)}</dd></div>
            </dl>
            {["paid", "partial"].includes(s.payment_status) && s.status !== "refunded" && !s.package_redemption && (
              <button onClick={() => setRefundSale(s)} data-testid={`sale-refund-${s.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs mt-3"><RotateCcw size={13} /> Refund</button>
            )}
          </div>
        ))}
        {data.length === 0 && <p className="text-center text-[#4A5D6B] py-10 eyw-card">No sales yet.</p>}
      </div>
      {refundSale && <RefundModal sale={refundSale} onClose={() => setRefundSale(null)} onDone={() => qc.invalidateQueries({ queryKey: ["pos-sales"] })} />}
    </div>
  );
}

const STATUS_TINT = {
  pending: "bg-[#E0A458]/15 text-[#B5794E]",
  confirmed: "bg-[#144F75]/10 text-[#144F75]",
  checked_in: "bg-[#5C8A72]/15 text-[#5C8A72]",
  in_session: "bg-[#9E5C7F]/15 text-[#9E5C7F]",
  completed: "bg-[#5C8A72]/20 text-[#4A7360]",
  no_show: "bg-[#B45151]/15 text-[#B45151]",
  refunded: "bg-[#B45151]/15 text-[#B45151]",
};

const canRefundRole = (user) => ["admin", "location_manager"].includes(user?.role) || !!user?.manages_location_id;
const apptRefundable = (a) => a.status !== "refunded" && a.payment_status !== "refunded" &&
  (["paid", "package", "partially_refunded"].includes(a.payment_status) || Number(a.amount_paid || 0) > 0);

const fmtDay = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }); }
  catch { return iso; }
};

function POSSchedule() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [range, setRange] = useState("day");
  const [noShow, setNoShow] = useState(null);
  const [refundAppt, setRefundAppt] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [reschedule, setReschedule] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["pos-appointments", range],
    queryFn: () => api.get("/pos/appointments", { params: { range } }).then((r) => r.data),
    refetchInterval: 60000,
  });

  const setStatus = async (id, status) => {
    if (status === "no_show") { setNoShow(id); return; }
    const { data: res } = await api.put(`/pos/appointments/${id}/status`, { status });
    const refresh = () => { qc.invalidateQueries({ queryKey: ["pos-appointments", range] }); qc.invalidateQueries({ queryKey: ["pos-balance-due"] }); };
    refresh();
    notifyStatusResult(res, status, id, qc, refresh);
  };

  const { data: balanceDue } = useQuery({
    queryKey: ["pos-balance-due"],
    queryFn: () => api.get("/pos/balance-due").then((r) => r.data),
    refetchInterval: 60000,
  });

  const collect = async (id, method) => {
    try {
      const { data: res } = await api.post(`/pos/appointments/${id}/collect`, { method });
      toast.success(res.already_paid ? "Already paid" : `Collected ${money(Number(res.collected || 0))} (${method})`);
      qc.invalidateQueries({ queryKey: ["pos-appointments", range] });
      qc.invalidateQueries({ queryKey: ["pos-balance-due"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record payment");
    }
  };

  const days = data?.days || [];
  return (
    <div data-testid="pos-schedule">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h3 className="font-serif text-xl text-[#1A242B]">Upcoming appointments</h3>
          {data && <p className="text-sm text-[#4A5D6B]">{data.total} appointment{data.total === 1 ? "" : "s"} · {data.start} → {data.end}</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex max-w-full overflow-x-auto scroll-x-hidden rounded-full bg-[#F1EEE8] p-0.5" data-testid="pos-sched-range">
            {[{ k: "day", l: "Today" }, { k: "week", l: "This week" }, { k: "month", l: "This month" }].map((r) => (
              <button key={r.k} onClick={() => setRange(r.k)} data-testid={`pos-sched-${r.k}`}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${range === r.k ? "bg-[#144F75] text-white" : "text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>
                {r.l}
              </button>
            ))}
          </div>
          <button onClick={() => setAddOpen(true)} data-testid="pos-add-appointment-btn" className="eyw-btn !py-2 text-sm shrink-0">
            <CalendarPlus size={15} /> Add Appointment
          </button>
        </div>
      </div>

      {balanceDue && balanceDue.count > 0 && (
        <div className="eyw-card p-4 mb-5 flex items-center justify-between flex-wrap gap-2 border-l-4 border-[#E0A458]" data-testid="pos-balance-due-strip">
          <div className="text-sm text-[#4A5D6B]">
            <span className="font-semibold text-[#1A242B]">Collect at door:</span> {balanceDue.count} appointment{balanceDue.count === 1 ? "" : "s"} with a balance
          </div>
          <div className="text-sm font-semibold text-[#9a6b1f]" data-testid="pos-balance-due-total">{money(Number(balanceDue.total_outstanding || 0))} outstanding</div>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>
      ) : days.length === 0 ? (
        <div className="eyw-card p-10 text-center text-[#4A5D6B]" data-testid="pos-sched-empty">No appointments in this window.</div>
      ) : (
        <div className="space-y-6">
          {days.map((d) => (
            <div key={d.date} className="eyw-card p-5" data-testid={`pos-sched-day-${d.date}`}>
              <div className="flex items-center gap-2 mb-3">
                <CalendarCheck size={15} className="text-[#144F75]" />
                <h4 className="font-semibold text-[#1A242B]">{fmtDay(d.date)}</h4>
                <span className="text-xs text-[#8FA89B]">· {d.appointments.length}</span>
              </div>
              <ul className="divide-y divide-[#F1EEE8]">
                {d.appointments.map((a) => (
                  <li key={a.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`pos-sched-appt-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1A242B] truncate">
                        <span className="tabular-nums text-[#8FA89B] mr-2">{fmtTimeRange(a.start_time)}</span>{a.service_title || "Appointment"}
                      </div>
                      <div className="text-xs text-[#4A5D6B]">{a.name || a.client || "—"}{a.enhancer_name ? ` · ${a.enhancer_name}` : ""}</div>
                      {(() => { const p = paymentSummary(a); return <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${PAYMENT_TONE[p.tone]}`} data-testid={`pos-sched-payment-${a.id}`}>{p.label}</span>; })()}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(() => { const p = paymentSummary(a); return p.owed > 0 ? (
                        <div className="flex gap-1" data-testid={`pos-collect-${a.id}`}>
                          <button title="Collect cash" onClick={() => collect(a.id, "cash")} data-testid={`pos-collect-cash-${a.id}`} className="h-8 px-2 grid place-items-center rounded-lg text-[#3f6b53] bg-[#5C8A72]/12 hover:bg-[#5C8A72]/20 text-xs font-semibold">Cash</button>
                          <button title="Collect card" onClick={() => collect(a.id, "card")} data-testid={`pos-collect-card-${a.id}`} className="h-8 px-2 grid place-items-center rounded-lg text-[#144F75] bg-[#144F75]/10 hover:bg-[#144F75]/20 text-xs font-semibold">Card</button>
                        </div>
                      ) : null; })()}
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_TINT[a.status] || STATUS_TINT.pending}`}>{(a.status || "").replace("_", " ")}</span>
                      {canRefundRole(user) && apptRefundable(a) && (
                        <button title={a.payment_status === "package" ? "Restore credit" : "Refund"} onClick={() => setRefundAppt(a.id)} data-testid={`pos-sched-refund-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#B45151] hover:bg-[#B45151]/10"><RotateCcw size={15} /></button>
                      )}
                      {!["cancelled", "completed", "no_show"].includes(a.status) && (
                        <div className="flex gap-1">
                          <button title="Reschedule" onClick={() => setReschedule(a)} data-testid={`pos-sched-reschedule-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#4A5D6B] hover:bg-[#8FA89B]/15"><CalendarClock size={15} /></button>
                          <button title="Check in" onClick={() => setStatus(a.id, "checked_in")} data-testid={`pos-sched-checkin-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#144F75] hover:bg-[#144F75]/10"><UserCheck size={15} /></button>
                          <button title="Complete" onClick={() => setStatus(a.id, "completed")} data-testid={`pos-sched-complete-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#5C8A72] hover:bg-[#5C8A72]/10"><CheckCircle2 size={15} /></button>
                          <button title="No-show" onClick={() => setStatus(a.id, "no_show")} data-testid={`pos-sched-noshow-${a.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-[#B5794E] hover:bg-[#D98E73]/10"><XCircle size={15} /></button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {noShow && <NoShowModal bookingId={noShow} onClose={() => setNoShow(null)} onDone={() => { setNoShow(null); qc.invalidateQueries({ queryKey: ["pos-appointments", range] }); }} />}
      {refundAppt && <AppointmentRefundModal bookingId={refundAppt} onClose={() => setRefundAppt(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["pos-appointments", range] }); qc.invalidateQueries({ queryKey: ["pos-dashboard"] }); }} />}
      {addOpen && <AddAppointmentModal onClose={() => setAddOpen(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ["pos-appointments", range] }); qc.invalidateQueries({ queryKey: ["pos-balance-due"] }); qc.invalidateQueries({ queryKey: ["pos-dashboard"] }); }} />}
      {reschedule && <RescheduleModal appt={reschedule} onClose={() => setReschedule(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["pos-appointments", range] }); qc.invalidateQueries({ queryKey: ["pos-dashboard"] }); }} />}
    </div>
  );
}

export default function POS() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const ctx = useActiveContext();
  const isAdminRole = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const isPlainEnhancer = user?.role === "enhancer" && !user?.manages_location_id;
  const [newEvent, setNewEvent] = useState(false);
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["pos-u-me"], queryFn: () => api.get("/pos/u/me").then((r) => r.data) });
  const centers = ctx?.centers || [];
  const activeCenterId = ctx?.activeCenterId || "";
  const activeCenterName = ctx?.activeCenterName || "";
  const needCenter = isAdminRole && (!activeCenterId || activeCenterId === "__all__");
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (["desk", "schedule", "outstanding", "giftcards", "register", "clients", "sales", "balances", "inventory", "events", "reports"].includes(t)) return t;
    return user?.role === "enhancer" && !user?.manages_location_id ? "schedule" : "desk";
  });
  const isManager = user?.role === "location_manager";
  // Location managers don't manage global inventory (vendors/POs are admin-scoped).
  const ENHANCER_TABS = ["schedule", "outstanding", "giftcards", "clients"];
  const visibleTabs = isPlainEnhancer ? TABS.filter((t) => ENHANCER_TABS.includes(t.key))
    : isManager || isStaff ? TABS.filter((t) => t.key !== "inventory") : TABS;
  const { data: balanceCount = 0 } = useQuery({
    queryKey: ["pos-balances-count", activeCenterId],
    queryFn: () => api.get("/pos/balances-today").then((r) => r.data.length),
    refetchInterval: 60000,
    enabled: !needCenter && !isPlainEnhancer,
  });

  const deskLabel = isManager || isStaff ? (me?.location_name || user?.manages_location_name) : isPlainEnhancer ? "My clients" : activeCenterName;

  if (isStaff && me?.unassigned) {
    return (
      <div className="min-h-screen bg-[#F9F7F3] grid place-items-center px-6" data-testid="pos-staff-unassigned">
        <div className="max-w-md text-center">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-[#144F75]/10 mx-auto mb-4"><MapPin size={24} className="text-[#144F75]" /></span>
          <h1 className="font-serif text-3xl text-[#1A242B] mb-2">You're not assigned to a center yet</h1>
          <p className="text-[#4A5D6B] mb-6">The register opens for the center you work at. Ask an admin to assign your center under Admin → Users, then come back here.</p>
          <button onClick={() => { logout(); nav("/"); }} className="eyw-btn-ghost" data-testid="pos-unassigned-logout">Log out</button>
        </div>
      </div>
    );
  }

  if (needCenter) {
    return (
      <div className="min-h-screen bg-[#F9F7F3]">
        <header className="bg-[#144F75] text-white">
          <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="https://customer-assets.emergentagent.com/job_eyw-admin-suite/artifacts/3ze0y6kq_EnhancingYourWorld%20site%20logo%20with%20transparent%20background%20web%20version.png" alt="Enhancing Your World" className="h-9 w-auto" />
              <span className="font-serif text-lg">Front Desk &amp; POS</span>
            </div>
            <button onClick={() => nav("/admin")} className="text-sm text-white/80 hover:text-white">Admin</button>
          </div>
        </header>
        <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="pos-center-gate">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-[#144F75]/10 mx-auto mb-4"><LayoutDashboard size={24} className="text-[#144F75]" /></span>
          <h1 className="font-serif text-3xl text-[#1A242B] mb-2">Choose a center to open the Front Desk</h1>
          <p className="text-[#4A5D6B] mb-6">A register is tied to one location. Pick which center you're operating today — you can switch anytime from the context menu up top.</p>
          <div className="space-y-2 text-left">
            {centers.map((c) => (
              <button key={c.id} onClick={() => ctx.setActiveCenter(c.id)} data-testid={`pos-gate-center-${c.id}`}
                className="w-full eyw-card p-4 flex items-center gap-3 hover:border-[#144F75] transition-colors text-left">
                <span className="grid place-items-center h-9 w-9 rounded-full bg-[#D98E73]/15"><MapPin size={16} className="text-[#D98E73]" /></span>
                <span className="min-w-0">
                  <span className="block font-medium text-[#1A242B] truncate">{c.name}</span>
                  {(c.city || c.state) && <span className="block text-xs text-[#8FA89B] truncate">{[c.city, c.state].filter(Boolean).join(", ")}</span>}
                </span>
              </button>
            ))}
            {centers.length === 0 && <p className="text-sm text-[#4A5D6B] text-center">No centers available.</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F3]" key={activeCenterId}>
      <header className="bg-[#144F75] text-white">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 min-h-16 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="https://customer-assets.emergentagent.com/job_eyw-admin-suite/artifacts/3ze0y6kq_EnhancingYourWorld%20site%20logo%20with%20transparent%20background%20web%20version.png" alt="Enhancing Your World" className="h-9 w-auto shrink-0" />
            <span className="font-serif text-base sm:text-lg whitespace-nowrap">Front Desk &amp; POS</span>
            {deskLabel && <span data-testid="pos-active-center" className="text-sm text-white/60 hidden md:block truncate">· {deskLabel}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0 max-w-full">
            <ContextSwitcher />
            <span className="text-sm text-white/70 hidden lg:block">{user?.email}</span>
            {isAdminRole && <button onClick={() => nav("/admin")} data-testid="pos-admin-btn" className="text-sm text-white/80 hover:text-white">Admin</button>}
            {isManager && <button onClick={() => nav("/manager")} data-testid="pos-manager-btn" className="text-sm text-white/80 hover:text-white">My Center</button>}
            <button onClick={() => { logout(); nav("/"); }} data-testid="pos-logout" className="inline-flex items-center gap-1.5 text-sm rounded-full bg-white/10 px-4 py-2 hover:bg-white/20"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
        <UnifiedSearch />
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-start gap-2 mb-8">
        <div className="flex gap-2 flex-1 min-w-0 overflow-x-auto scroll-x-hidden pb-1 sm:flex-wrap">
          {visibleTabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`pos-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all shrink-0 ${tab === t.key ? "bg-[#144F75] text-white shadow" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>
              <t.icon size={16} /> {t.label}
              {t.key === "balances" && balanceCount > 0 && (
                <span data-testid="pos-balances-badge" className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${tab === t.key ? "bg-white text-[#144F75]" : "bg-[#B5794E] text-white"}`}>{balanceCount}</span>
              )}
            </button>
          ))}
        </div>
          <button onClick={() => setNewEvent(true)} data-testid="pos-new-event-btn" title="Create a one-time or repeating event right from the front desk"
            className="inline-flex items-center gap-2 rounded-full px-4 sm:px-5 py-2.5 text-sm shrink-0 bg-[#D98E73] text-white hover:bg-[#c67b60] transition-colors">
            <CalendarPlus size={16} /> <span className="hidden sm:inline">New event</span><span className="sm:hidden">Event</span>
          </button>
        </div>
        {newEvent && <EventFormModal event={null} onClose={() => setNewEvent(false)} onSaved={() => { setNewEvent(false); qc.invalidateQueries(); }} />}
        {tab === "desk" && <FrontDesk />}
        {tab === "schedule" && <POSSchedule />}
        {tab === "outstanding" && <POSOutstanding />}
        {tab === "giftcards" && <POSGiftCards />}
        {tab === "register" && <POSRegister />}
        {tab === "clients" && <POSClients />}
        {tab === "sales" && <SalesList />}
        {tab === "balances" && <POSBalances />}
        {tab === "inventory" && <POSInventory />}
        {tab === "events" && <POSEvents />}
        {tab === "reports" && <POSReports />}
      </div>
    </div>
  );
}
